/**
 * Calendar — `/calendar` route (Phase G3C-A CP2).
 *
 * Two viewing modes:
 *   - Month grid (default) — react-day-picker with per-day dots.
 *     Click a populated day → opens `<DayDrawer />` listing that
 *     day's events.
 *   - List view — chronological rows, each an `<EventCard />`.
 *
 * Data source: `GET /api/calendar?start_date&end_date&include_dismissed`.
 * The month grid + list share the same fetch — no double calls.
 *
 * State transitions handled by this page:
 *   - Dismiss (auto/manual, persisted) → `POST /calendar/events/{id}/dismiss`
 *   - Edit (manual only)               → opens the same modal as
 *                                        `AddReminderModal` in edit mode
 *   - Delete (manual only)             → `DELETE /calendar/events/{id}`
 *
 * Synthesized `document_expiry` rows are non-mutable — the frontend
 * enforces (no affordance rendered) AND the backend enforces (400
 * `synthesized_event_not_mutable` on any mutation attempt).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar as CalendarIcon,
  List,
  Loader2,
  Plus,
  AlertTriangle,
  X,
} from "lucide-react";
import { toast } from "sonner";
import CalendarShell from "@/components/calendar/CalendarShell";
import FilterChips from "@/components/calendar/FilterChips";
import MonthView from "@/components/calendar/MonthView";
import DayDrawer from "@/components/calendar/DayDrawer";
import EventCard from "@/components/calendar/EventCard";
import AddReminderModal from "@/components/calendar/AddReminderModal";
import EditReminderModal from "@/components/calendar/EditReminderModal";
import {
  dateKey,
  groupByDate,
  DEFAULT_SOURCES,
  DEFAULT_TYPES,
} from "@/components/calendar/utils";
import { api, formatApiError } from "@/api/client";

const VIEWS = { MONTH: "month", LIST: "list" };

export default function Calendar() {
  const [view, setView] = useState(VIEWS.MONTH);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [includeDismissed, setIncludeDismissed] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Query the widest reasonable range so the month grid can page
      // forwards/backwards without a re-fetch. Backend defaults are
      // -90d / +365d; we expand slightly forward to 400d so annual
      // renewal events are always visible on the current month view.
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 120);
      const end = new Date(now);
      end.setDate(end.getDate() + 400);
      const res = await api.get("/calendar", {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          include_dismissed: includeDismissed,
        },
      });
      setItems(res.data.items || []);
    } catch (err) {
      setError(formatApiError(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [includeDismissed]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Apply user filters — filter chips affect both views.
  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (!sources.includes(it.event_source)) return false;
      if (!types.includes(it.event_type)) return false;
      return true;
    });
  }, [items, sources, types]);

  const activeItems = useMemo(
    () => filteredItems.filter((i) => i.status === "active"),
    [filteredItems],
  );
  const dismissedItems = useMemo(
    () => filteredItems.filter((i) => i.status === "dismissed"),
    [filteredItems],
  );

  const dayBuckets = useMemo(
    () => groupByDate(activeItems),
    [activeItems],
  );

  function toggleSource(key) {
    setSources((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );
  }
  function toggleType(key) {
    setTypes((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );
  }

  function handleDayClick(d) {
    setSelectedDate(d);
    setDrawerOpen(true);
  }

  async function handleDismiss(event) {
    try {
      const res = await api.post(`/calendar/events/${event.id}/dismiss`);
      setItems((cur) => cur.map((it) => (it.id === event.id ? res.data : it)));
      toast.success("Event dismissed.");
      // If we're on the day drawer, refresh the selected day view.
      if (!includeDismissed && drawerOpen) {
        // Item now belongs to the dismissed bucket — drawer auto-updates.
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleDelete(event) {
    try {
      await api.delete(`/calendar/events/${event.id}`);
      setItems((cur) => cur.filter((it) => it.id !== event.id));
      toast.success("Reminder deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function handleEdit(event) {
    setEditingEvent(event);
  }

  function handleCreated(newEvent) {
    setItems((cur) => [newEvent, ...cur]);
    toast.success("Reminder added.");
  }

  function handleUpdated(updated) {
    setItems((cur) => cur.map((it) => (it.id === updated.id ? updated : it)));
    setEditingEvent(null);
    toast.success("Reminder updated.");
  }

  // Day drawer's list — always show live-state items for the selected day.
  const drawerItems = useMemo(() => {
    if (!selectedDate) return [];
    const key = dateKey(selectedDate);
    return dayBuckets[key] || [];
  }, [dayBuckets, selectedDate]);

  const isEmpty = !loading && !error && items.length === 0;
  const activeVisible = activeItems.length > 0;
  const showDismissedSection = includeDismissed && dismissedItems.length > 0;

  return (
    <CalendarShell>
      <header className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
            Calendar
          </p>
          <h1 className="mt-2 text-[36px] sm:text-[48px] font-semibold tracking-tight text-v2-text">
            Deadlines &amp; reminders
          </h1>
          <p className="mt-3 text-sm text-v2-textMuted max-w-2xl">
            Every upcoming grant deadline, annual renewal window, and document
            refresh in one place. Add your own reminders anytime.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex items-center rounded-full p-1"
            style={{
              background: "var(--v2-surface)",
              border: "1px solid var(--v2-border)",
            }}
          >
            <button
              type="button"
              onClick={() => setView(VIEWS.MONTH)}
              data-testid="calendar-view-toggle-month"
              data-active={view === VIEWS.MONTH ? "true" : "false"}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{
                background:
                  view === VIEWS.MONTH ? "rgba(167,139,250,0.14)" : "transparent",
                color:
                  view === VIEWS.MONTH ? "var(--v2-accent)" : "var(--v2-textMuted)",
              }}
            >
              <CalendarIcon className="w-3.5 h-3.5" /> Month
            </button>
            <button
              type="button"
              onClick={() => setView(VIEWS.LIST)}
              data-testid="calendar-view-toggle-list"
              data-active={view === VIEWS.LIST ? "true" : "false"}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{
                background:
                  view === VIEWS.LIST ? "rgba(167,139,250,0.14)" : "transparent",
                color:
                  view === VIEWS.LIST ? "var(--v2-accent)" : "var(--v2-textMuted)",
              }}
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="calendar-add-reminder-open"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-colors"
            style={{ background: "rgba(167,139,250,0.9)" }}
          >
            <Plus className="w-3.5 h-3.5" /> Add reminder
          </button>
        </div>
      </header>

      {error && (
        <div
          data-testid="calendar-error-banner"
          role="alert"
          className="mt-6 rounded-2xl p-4 flex items-start gap-3"
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.35)",
          }}
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
          <p className="flex-1 text-sm text-v2-text leading-relaxed">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            data-testid="calendar-error-banner-dismiss"
            aria-label="Dismiss error"
            className="flex-shrink-0 text-v2-textMuted hover:text-v2-text"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mt-6">
        <FilterChips
          sources={sources}
          onToggleSource={toggleSource}
          types={types}
          onToggleType={toggleType}
          includeDismissed={includeDismissed}
          onToggleDismissed={() => setIncludeDismissed((v) => !v)}
        />
      </div>

      {loading && (
        <div
          data-testid="calendar-loading"
          className="mt-10 flex items-center gap-2 text-sm text-v2-textMuted"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your calendar…
        </div>
      )}

      {isEmpty && (
        <div
          data-testid="calendar-empty-state"
          className="mt-10 rounded-3xl p-10 text-center"
          style={{
            background: "var(--v2-surface)",
            border: "1px dashed var(--v2-border)",
          }}
        >
          <CalendarIcon className="mx-auto w-6 h-6 text-v2-accent" />
          <p className="mt-3 text-v2-text font-semibold">
            No upcoming events.
          </p>
          <p className="mt-2 text-sm text-v2-textMuted">
            Save grants to see deadlines and renewals here.
          </p>
        </div>
      )}

      {!loading && !isEmpty && (
        <>
          {view === VIEWS.MONTH ? (
            <div className="mt-6 space-y-4">
              <MonthView
                items={activeItems}
                month={month}
                onMonthChange={setMonth}
                selectedDate={selectedDate}
                onDayClick={handleDayClick}
              />
              <MonthLegend />
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              {activeVisible ? (
                <ul
                  data-testid="calendar-list-view"
                  className="space-y-3"
                >
                  {activeItems.map((ev) => (
                    <EventCard
                      key={ev.id}
                      event={ev}
                      onDismiss={handleDismiss}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </ul>
              ) : (
                <p
                  data-testid="calendar-list-empty-filtered"
                  className="text-sm text-v2-textMuted"
                >
                  No events match the selected filters.
                </p>
              )}
              {showDismissedSection && (
                <section data-testid="calendar-dismissed-section" className="pt-2">
                  <h2 className="text-[11px] uppercase tracking-[0.18em] text-v2-textMuted">
                    Dismissed events
                  </h2>
                  <ul className="mt-3 space-y-3">
                    {dismissedItems.map((ev) => (
                      <EventCard
                        key={ev.id}
                        event={ev}
                        onDismiss={handleDismiss}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </>
      )}

      <DayDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        date={selectedDate}
        items={drawerItems}
        onDismiss={handleDismiss}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <AddReminderModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={handleCreated}
      />

      <EditReminderModal
        event={editingEvent}
        onOpenChange={(open) => !open && setEditingEvent(null)}
        onUpdated={handleUpdated}
      />
    </CalendarShell>
  );
}

/**
 * Small horizontal legend below the month grid explaining the dot
 * colour → event type mapping. No emoji, no gaudy iconography — just
 * the same coloured dots the grid uses.
 */
function MonthLegend() {
  const rows = [
    { color: "rgb(167,139,250)", label: "Grant deadline" },
    { color: "rgb(56,189,248)", label: "Grant renewal" },
    { color: "rgb(251,191,36)", label: "Document refresh (auto)" },
    { color: "rgb(148,163,184)", label: "Personal reminder" },
  ];
  return (
    <div
      data-testid="calendar-month-legend"
      className="flex items-center gap-4 flex-wrap text-[11px] text-v2-textMuted"
    >
      {rows.map((r) => (
        <span key={r.label} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: r.color }}
            aria-hidden
          />
          {r.label}
        </span>
      ))}
    </div>
  );
}
